import { Modal } from "antd";
import { useNavigate } from "react-router-dom";
import moment from "moment";

const STATUS_PILL = {
  active: "bg-green-100 text-green-700",
  upcoming: "bg-amber-100 text-amber-700",
  expired: "bg-gray-100 text-gray-600",
  cancelled: "bg-red-100 text-red-700",
};

const humanize = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const restrictionText = (f) => {
  if (!f || !f.enabled) return "-";
  switch (f.access) {
    case "count": return `${f.count || 0} times`;
    case "once": return "Only once";
    case "days": return `${f.days || 0} days`;
    case "full":
    default: return "Full access";
  }
};

const usageInfo = (f, used) => {
  const u = used || 0;
  if (!f) return null;
  if (f.access === "count") {
    const lim = f.count || 0;
    const left = Math.max(0, lim - u);
    return `used ${u} / ${lim}${left === 0 ? " · exhausted" : ` · ${left} left`}`;
  }
  if (f.access === "once") {
    return `used ${u} / 1${u >= 1 ? " · exhausted" : " · 1 left"}`;
  }
  return `used ${u}`; // days / full (no cap)
};

const fmtDate = (v) => (v ? moment(v).format("DD-MMM-YYYY HH:mm") : "-");
const fmtAmount = (v) => `₹${v ? Number(v).toLocaleString("en-IN") : 0}`;

// A single "label ............ value" row
const Field = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-gray-100 last:border-b-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-sm font-medium text-gray-800 text-right break-all">{value ?? "-"}</span>
  </div>
);

// A card section
const SectionCard = ({ title, children, full }) => (
  <div className={`rounded-xl border border-gray-200 bg-white p-4 shadow-sm ${full ? "md:col-span-2" : ""}`}>
    <div className="text-[12px] font-bold uppercase tracking-wide text-[#055392] mb-1 pb-1.5 border-b border-gray-100">
      {title}
    </div>
    {children}
  </div>
);

// A clickable inline link
const LinkText = ({ onClick, children }) => (
  <button type="button" onClick={onClick} className="text-blue-600 hover:text-blue-800 underline break-all text-left">
    {children}
  </button>
);

// Professional card-based popup showing the DB info for one purchased
// subscription (a UserSubscription record). Styled with Tailwind.
export default function SubscriptionPurchaseModal({ data, open, onClose }) {
  const d = data || {};
  const navigate = useNavigate();
  const features = d.features || {};
  const enabledFeatures = Object.keys(features).filter((k) => features[k] && features[k].enabled);
  const usage = d.feature_usage || {};
  const hasUsage = !!d.feature_usage;
  const segName = d.segment_id?.en_name || d.segment_en_name;

  // close the modal, then navigate
  const go = (path) => { if (onClose) onClose(); navigate(path); };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={780}
      centered
      title="Subscription Purchase Details"
      footer={
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg bg-[#055392] text-white text-sm font-semibold hover:bg-[#044070] transition-colors"
          >
            Close
          </button>
        </div>
      }
    >
      <div className="max-h-[72vh] overflow-y-auto pr-4 pb-4">

        {/* Header summary */}
        <div className="flex justify-between items-start gap-4 rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50 to-sky-50 px-4 py-4">
          <div>
            {d.subscription_id ? (
              <button type="button" onClick={() => go(`/subscription/view/${d.subscription_id}`)} className="text-lg font-bold text-[#0b2b45] hover:underline text-left">
                {d.en_name || "Subscription Plan"}
              </button>
            ) : (
              <div className="text-lg font-bold text-[#0b2b45]">{d.en_name || "Subscription Plan"}</div>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {segName && (
                <button type="button" onClick={() => go(`/segment`)} className="inline-block rounded-full bg-blue-100 text-blue-700 text-[11px] font-semibold px-2.5 py-0.5 hover:bg-blue-200">{segName}</button>
              )}
              {d.type && (
                <span className="inline-block rounded-full bg-gray-100 text-gray-600 text-[11px] font-semibold px-2.5 py-0.5 capitalize">{d.type}</span>
              )}
              <span className={`inline-block rounded-full text-[11px] font-semibold px-2.5 py-0.5 capitalize ${STATUS_PILL[d.status] || "bg-gray-100 text-gray-600"}`}>
                {d.status || "-"}
              </span>
            </div>
          </div>
          <div className="text-right whitespace-nowrap">
            <div className="text-2xl font-extrabold text-emerald-600">{fmtAmount(d.amount_paid)}</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {d.selected_duration ? `${d.selected_duration} Month${d.selected_duration > 1 ? "s" : ""}` : ""}
            </div>
          </div>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">

          <SectionCard title="Purchase Summary">
            <Field label="Duration" value={d.selected_duration ? `${d.selected_duration} Month${d.selected_duration > 1 ? "s" : ""}` : "-"} />
            <Field label="Amount Paid" value={fmtAmount(d.amount_paid)} />
            <Field label="Currency" value={d.currency || "INR"} />
            <Field label="Status" value={<span className="capitalize">{d.status || "-"}</span>} />
            <Field label="Remaining Days" value={d.remaining_days ?? "-"} />
            <Field label="Valid From" value={fmtDate(d.start_date)} />
            <Field label="Valid Till" value={fmtDate(d.end_date)} />
          </SectionCard>

          <SectionCard title="Plan">
            <Field label="Plan (EN)" value={d.subscription_id ? <LinkText onClick={() => go(`/subscription/view/${d.subscription_id}`)}>{d.en_name || "-"}</LinkText> : (d.en_name || "-")} />
            <Field label="Plan (HI)" value={d.hi_name || "-"} />
            <Field label="Plan (GJ)" value={d.gj_name || "-"} />
            <Field label="Type" value={<span className="capitalize">{d.type || "-"}</span>} />
          </SectionCard>

          <SectionCard title="User Snapshot">
            <Field label="Name" value={d.user_name || "-"} />
            <Field label="Phone" value={d.user_phone || "-"} />
            <Field label="Email" value={d.user_email || "-"} />
          </SectionCard>

          <SectionCard title="Segment Snapshot">
            <Field label="Segment (EN)" value={d.segment_en_name || "-"} />
            <Field label="Segment (HI)" value={d.segment_hi_name || "-"} />
            <Field label="Segment (GJ)" value={d.segment_gj_name || "-"} />
          </SectionCard>

          <SectionCard title="Payment">
            <Field label="Transaction ID" value={d.transaction_id ? <LinkText onClick={() => go(`/transaction/view/${d.transaction_id}`)}>{d.transaction_id}</LinkText> : "-"} />
            <Field label="Razorpay Payment ID" value={d.razorpay_payment_id || "-"} />
          </SectionCard>

          <SectionCard title="Meta">
            <Field label="Record ID" value={d._id || "-"} />
            <Field label="Created At" value={fmtDate(d.created_at)} />
            <Field label="Updated At" value={fmtDate(d.updated_at)} />
          </SectionCard>

          <SectionCard title={`Features (${enabledFeatures.length} enabled)`} full>
            {enabledFeatures.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                {enabledFeatures.map((k) => {
                  const line = hasUsage ? usageInfo(features[k], usage[k]) : null;
                  return (
                    <Field key={k} label={humanize(k)} value={
                      <div className="flex flex-col items-end gap-0.5">
                        <span className="inline-block rounded-md bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5">
                          {restrictionText(features[k])}
                        </span>
                        {line ? <span className="text-[11px] text-gray-500">{line}</span> : null}
                      </div>
                    } />
                  );
                })}
              </div>
            ) : (
              <div className="py-3 text-sm text-gray-400 text-center">No features enabled</div>
            )}
          </SectionCard>

        </div>
      </div>
    </Modal>
  );
}
